import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import ApiTokens from './pages/ApiTokens';
import Workspace from './pages/Workspace';
import ProjectDetail from './pages/ProjectDetail';
import Offers from './pages/Offers';
import OfferDetail from './pages/OfferDetail';
import Invoices from './pages/Invoices';
import RecurringInvoices from './pages/RecurringInvoices';
import Banking from './pages/Banking';
import Documents from './pages/Documents';
import InvoiceDetail from './pages/InvoiceDetail';
import ChangePassword from './pages/ChangePassword';
import NotFound from './pages/NotFound';
import './App.css';

const App: React.FC = () => {
  return (
    <ThemeProvider>
    <Router>
      <AuthProvider>
        <div className="App">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'var(--surface-bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--surface-border)',
                boxShadow: '0 4px 16px -4px rgba(0, 0, 0, 0.12)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '10px',
                padding: '12px 16px',
              },
              success: {
                iconTheme: { primary: 'var(--badge-green-text)', secondary: 'white' },
              },
              error: {
                iconTheme: { primary: 'var(--badge-red-text)', secondary: 'white' },
              },
            }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspace"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Workspace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Offers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OfferDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Invoices />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/recurring"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecurringInvoices />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InvoiceDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-tokens"
              element={
                <ProtectedRoute requiredRole="ADMIN">
                  <Layout>
                    <ApiTokens />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChangePassword />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Keine Landing-Page: die Wurzel fuehrt direkt in die App bzw. zum Login */}
            <Route
              path="/banking"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Banking />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Documents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
    </ThemeProvider>
  );
};

export default App;
